import { ref } from 'vue'

export const SIDEBAR_SECTION_IDS = ['overview', 'operations'] as const
export type SidebarSectionId = (typeof SIDEBAR_SECTION_IDS)[number]

const STORAGE_KEY = 'lightbase.sidebar-nav'
const STORAGE_VERSION = 1

interface PersistedSidebarNav {
  version: number
  order: SidebarSectionId[]
  expanded: Partial<Record<SidebarSectionId, boolean>>
}

const DEFAULT_ORDER: SidebarSectionId[] = [...SIDEBAR_SECTION_IDS]
const DEFAULT_EXPANDED: Record<SidebarSectionId, boolean> = {
  overview: true,
  operations: true,
}

function isSectionId(value: unknown): value is SidebarSectionId {
  return typeof value === 'string' && (SIDEBAR_SECTION_IDS as readonly string[]).includes(value)
}

function normalizeOrder(raw: unknown): SidebarSectionId[] {
  const next: SidebarSectionId[] = []
  const seen = new Set<SidebarSectionId>()
  if (Array.isArray(raw)) {
    for (const id of raw) {
      if (isSectionId(id) && !seen.has(id)) {
        seen.add(id)
        next.push(id)
      }
    }
  }
  for (const id of DEFAULT_ORDER) {
    if (!seen.has(id)) next.push(id)
  }
  return next
}

function normalizeExpanded(raw: unknown): Record<SidebarSectionId, boolean> {
  const next = { ...DEFAULT_EXPANDED }
  if (!raw || typeof raw !== 'object') return next
  const record = raw as Partial<Record<string, unknown>>
  for (const id of SIDEBAR_SECTION_IDS) {
    if (typeof record[id] === 'boolean') next[id] = record[id]
  }
  return next
}

function readStorage(): { order: SidebarSectionId[]; expanded: Record<SidebarSectionId, boolean> } {
  const fallback = {
    order: [...DEFAULT_ORDER],
    expanded: { ...DEFAULT_EXPANDED },
  }
  if (typeof localStorage === 'undefined') return fallback
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<PersistedSidebarNav>
    return {
      order: normalizeOrder(parsed.order),
      expanded: normalizeExpanded(parsed.expanded),
    }
  } catch {
    return fallback
  }
}

function writeStorage(
  order: SidebarSectionId[],
  expanded: Record<SidebarSectionId, boolean>,
) {
  if (typeof localStorage === 'undefined') return
  const payload: PersistedSidebarNav = {
    version: STORAGE_VERSION,
    order,
    expanded,
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
}

const persisted = readStorage()
const order = ref<SidebarSectionId[]>(persisted.order)
const expanded = ref<Record<SidebarSectionId, boolean>>(persisted.expanded)

function persist() {
  writeStorage(order.value, expanded.value)
}

export function useSidebarNav() {
  function isExpanded(id: SidebarSectionId): boolean {
    return expanded.value[id] !== false
  }

  function toggle(id: SidebarSectionId) {
    expanded.value = { ...expanded.value, [id]: !isExpanded(id) }
    persist()
  }

  function move(fromId: SidebarSectionId, toId: SidebarSectionId, place: 'before' | 'after') {
    const current = order.value
    const fromIndex = current.indexOf(fromId)
    const toIndex = current.indexOf(toId)
    if (fromIndex < 0 || toIndex < 0) return

    let insertAt = place === 'before' ? toIndex : toIndex + 1
    if (fromIndex < insertAt) insertAt -= 1
    if (insertAt === fromIndex) return

    const next = [...current]
    next.splice(fromIndex, 1)
    next.splice(insertAt, 0, fromId)
    order.value = next
    persist()
  }

  function visibleOrder(visibleIds: SidebarSectionId[]): SidebarSectionId[] {
    const visible = new Set(visibleIds)
    return order.value.filter((id) => visible.has(id))
  }

  return {
    order,
    expanded,
    isExpanded,
    toggle,
    move,
    visibleOrder,
  }
}
